<template>
  <DialogTemplate
    align="center"
    class="dialog"
    :show-controls="true"
    v-bind="props"
    @close="emit('close')"
  >
    <template #header>
      <div class="graphics">
        <DialogGamingLevelGraphicConfetti
          ref="confettiElement"
          class="confetti"
        />
        <DialogGamingLevelGraphicCockade ref="cockadeElement" class="cockade" />
        <DialogGamingLevelGraphicBackground
          ref="backgroundElement"
          class="background"
        />
        <span ref="valueElement" class="value">{{ gaming.level }}</span>
      </div>
      <div class="header">
        <span ref="titleElement" class="title">Niveau débloqué</span>
      </div>
    </template>
    <p ref="xpElement" class="xp">
      Tu as atteint
      <span class="xp-value">{{ gaming.xp }} <LogoXP class="logo" /></span> !
    </p>
    <p ref="levelElement" class="level">
      Bravo, tu viens d'atteindre le niveau
      <span class="level-value">{{ gaming.level }}</span
      >, continue comme ça !
      <icon class="icon" name="fluent-emoji:smiling-face-with-sunglasses" />
    </p>
    <template #footer>
      <div class="actions">
        <EffectParticles />
        <ButtonBase
          ref="buttonElement"
          rounded
          theme="secondary"
          variant="tonal"
          stroke="n"
          to="/gaming#level"
          @click="() => dialog.close()"
        >
          Voir tous les niveaux
        </ButtonBase>
      </div>
    </template>
  </DialogTemplate>
</template>
<script lang="ts" setup>
import { gsap } from 'gsap';
interface Props {
  closeTimer?: number;
  closeClickOutside?: boolean;
  position?: 'center' | 'top-right';
}

const props = defineProps({
  closeTimer: { default: -1 },
  closeClickOutside: { type: Boolean, default: true },
  position: { default: 'center' }
});

const dialog = useDialogStore();

const gaming = useGamingStore();

const emit = defineEmits(["close"]);

const cockadeElement = ref(null);
const backgroundElement = ref(null);
const confettiElement = ref(null);
const valueElement = ref(null);
const titleElement = ref(null);
const xpElement = ref(null);
const levelElement = ref(null);
const buttonElement = ref(null);

const playAnimation = () => {
  const tl = gsap.timeline();
  tl.fromTo(
    cockadeElement.value?.$el,
    { scale: 0 },
    { rotation: 0, scale: 1, ease: 'elastic.out(1,0.3)', duration: 1 },
    0.1
  );
  tl.fromTo(
    backgroundElement.value?.$el,
    { opacity: 0 },
    { rotation: 0, z: 0, opacity: 1, ease: 'quad.out', duration: 0.1 },
    0.15
  );
  tl.fromTo(
    backgroundElement.value?.$el,
    { z: 300 },
    { rotation: 0, z: 0, ease: 'elastic.out(1,0.3)', duration: 2 },
    0.15
  );
  tl.fromTo(
    valueElement.value,
    { opacity: 0 },
    { opacity: 1, ease: 'quad.out', duration: 0.1 },
    0.2
  );
  tl.fromTo(
    valueElement.value,
    { z: -400 },
    { z: 0, ease: 'elastic.out(1,0.3)', duration: 2 },
    0.2
  );
  tl.fromTo(
    titleElement.value,
    { opacity: 0 },
    {
      opacity: 1,
      ease: 'quad.out',
      duration: 0.2,
    },
    0.4
  );
  tl.fromTo(
    titleElement.value,
    { z: 300 },
    {
      z: 0,
      ease: 'elastic.out(1,0.9)',
      duration: 1.5,
    },
    0.4
  );
  tl.fromTo(
    confettiElement.value.$el,
    { opacity: 0 },
    { opacity: 1, ease: 'quad.out', duration: 0.2 },
    0.4
  );
  tl.fromTo(
    confettiElement.value.$el,
    { z: -200 },
    { z: 0, ease: 'elastic.out(1,0.1)', duration: 3 },
    0.2
  );
  tl.fromTo(
    xpElement.value,
    { y: 4, opacity: 0 },
    { y: 0, opacity: 1, ease: 'quad.out', duration: 0.55 },
    0.6
  );
  tl.fromTo(
    levelElement.value,
    { y: 3, opacity: 0 },
    { y: 0, opacity: 1, ease: 'quad.out', duration: 0.6 },
    0.7
  );
  tl.fromTo(
    buttonElement.value.$el,
    { opacity: 0 },
    { opacity: 1, ease: 'quad.out', duration: 0.2 },
    0.5
  );
  tl.fromTo(
    buttonElement.value.$el,
    { scale: 0.5 },
    { scale: 1, ease: 'elastic.out(1,0.5)', duration: 1.2 },
    0.5
  );
};

watch(
  () => cockadeElement.value,
  (element) => {
    if (element?.$el) {
      playAnimation();
    }
  }
);
</script>
<style lang="scss" scoped>
.dialog {
  position: relative;
  width: 100%;
  @include above('l') {
    max-width: 40rem;
  }
}
.graphics {
  position: absolute;
  top: 0;
  left: 50%;
  width: auto;
  width: 10rem;
  height: 10rem;
  transform: translate(-50%, -50%);
  perspective: 800px;
  .value {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 0;
    width: 100%;
    margin-top: -2rem;
    color: var(--color_neutral_50);
    text-align: center;
    @include font('xl', 600, 'secondary');
  }
  .confetti,
  .background,
  .cockade {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    height: 100%;
  }
  .background {
    width: 6rem;
    height: 6rem;
    margin-top: -3rem;
    margin-left: -3rem;
  }
  .cockade {
    width: 10.7rem;
    height: 10.3rem;
    margin-top: -5.1rem;
    margin-left: -5rem;
  }
  .confetti {
    width: 11.3rem;
    height: 6.5rem;
    margin-top: -11.1rem;
    margin-left: -5.8rem;
  }
}
.header {
  perspective: 1000px;
  perspective-origin: center bottom;
}
.title {
  display: block;
  margin-top: 4rem;
  @include font('l', 800, 'secondary');

  color: var(--color_secondray);
}
.xp {
  @include font('m', 800);

  color: var(--color_neutral_700);
  &-value {
    display: inline;
    color: var(--color_secondary);
  }
  .logo {
    display: inline-block;
    width: auto;
    height: 2.8rem;
  }
}
.level {
  @include font('n', 600);

  display: inline-block;
  width: 100%;
  color: var(--color_neutral_700);
  .icon {
    display: inline;
  }
  &-value {
    @include font('n', 800);

    color: var(--color_secondary);
  }
}
.actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}
</style>
